import { StrictMode } from 'react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { TooltipProvider } from '@radix-ui/react-tooltip'
import './index.css'
import { HelmetProvider } from 'react-helmet-async'
import { ThemeProvider } from './context/ThemeContext'
import App from './App'
import * as Sentry from '@sentry/react'
import posthog from 'posthog-js'

function initThirdParty() {
  if (!import.meta.env.PROD) return
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
  })
  // GDPR: autocapture disabled, memory-only persistence — no cookie consent needed
  posthog.init(import.meta.env.VITE_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_POSTHOG_HOST ?? 'https://eu.posthog.com',
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false,
    persistence: 'memory',
    disable_session_recording: true,
  })
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
})

const rootElement = document.getElementById('root')!
const app = (
  <StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <TooltipProvider delayDuration={300}>
            <App />
          </TooltipProvider>
        </HelmetProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </StrictMode>
)

// react-snap prerender: use hydrateRoot when static HTML exists, createRoot otherwise
// Third-party inits deferred after hydration to prevent React #418 hydration mismatch
if (rootElement.hasChildNodes()) {
  hydrateRoot(rootElement, app)
  setTimeout(initThirdParty, 0)
} else {
  initThirdParty()
  createRoot(rootElement).render(app)
}
